import { Input } from 'components/atoms';
import { FormInput, InfoBox, TabsNavigation } from 'components/molecules';
import LabeledLabelStatus from 'components/molecules/labeledStatus/LabeledLabelStatus';
import TopBar from 'components/organisms/top-bar/TopBar';
import { TopbarTabs } from 'components/templates';
import { Form, Formik, FormikConfig } from 'formik';
import i18next from 'i18next';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  CUSTOMER_TYPE,
  ILabelForm,
  LABEL_STATUS,
  RESIDENCE_CERTIFICATE_EXPIRATION_STATUS,
  SETTLEMENT_METHOD,
  TRUST_LEVEL,
} from 'services/labels/types';
import { useGetLicenceConfiguration } from 'services/settings/individual';
import { IAddress } from 'services/types';
import { getApplicationSettings } from 'store/state';
import { II18nextPrefix } from 'types/components/components.interface';
import { useEnumToSelectOptions } from 'utils/components/inputs/select';
import { isUnder18 } from 'utils/components/isUnder18';
import { getCountriesPhoneNumberPrefixOptionValues } from 'utils/global/countries.utils';
import {
  conditionalField,
  getTabError,
  requiredEnumSelect,
} from 'utils/global/validation.utils';
import { getLastPathname } from 'utils/navbar/getParentPathname';
import { object, string } from 'yup';
import LabelAdvance from '../advance/advanceLabel';
import { IFormikProps, IFormProps } from '../types';
import LabelAdvanceHistory from './tabs/LabelAdvanceHistory';
import LabelBalance from './tabs/LabelBalance';
import LabelBalanceHistory from './tabs/LabelBalanceHistory';
import LabelContracts from './tabs/LabelContracts';
import LabelDefaultContracts from './tabs/LabelDefaultContracts';
import LabelFinances from './tabs/LabelFinances';
import LabelRemarks from './tabs/LabelRemarks';
import LabelReports from './tabs/LabelReports';
import LabelReportsHistory from './tabs/LabelReportsHistory';
import LabelResidenceCertificate from './tabs/LabelResidenceCertificate';
import LabelSettlements from './tabs/LabelSettlements';
import LabelStatistics from './tabs/LabelStatistics';
import LabelSupervisor from './tabs/LabelSupervisor';
import LabelThreads from './tabs/LabelThreads';

export enum LabelTabs {
  finances,
  remarks,
  supervisor,
  contracts,
  defaultContracts,
  contractsHistory,
  residenceCertificates,
  advance,
  settlements,
  reports,
  balance,
  statistics,
  threads,
}

const addressInitalValues: IAddress = {
  street: '',
  houseNumber: '',
  flatNumber: '',
  zipCode: '',
  city: '',
  countryAlfa2: '',
};

export const labelInitialValues: ILabelForm = {
  labelStatus: LABEL_STATUS.Draft,
  customerType: CUSTOMER_TYPE.Company,
  labelName: '',
  email: '',
  phoneNumberPrefix: getApplicationSettings()?.phoneNumberPrefix ?? '',
  phoneNumber: '',
  firstName: '',
  lastName: '',
  dateOfBirth: new Date().toISOString().split('T')[0],
  address: addressInitalValues,
  taxOffice: {
    name: '',
    ...addressInitalValues,
  },
  legalSupervisorFirstName: '',
  legalSupervisorLastName: '',
  legalSupervisorCountryAlfa2: '',
  legalSupervisorPersonalId: '',
  bankAccountNumber: '',
  bankName: '',
  swift: '',
  personalIdCountryAlfa2: '',
  personalId: '',
  companyId: '',
  companyCountryAlfa2: '',
  notifyOnDocumentGeneration: false,
  settlementMethod: SETTLEMENT_METHOD.unknown,
  settlementDeductionPercentage: null,
  defaultContracts: [],
  residenceCertificates: [],
  residenceCertificateExpirationStatus: undefined,
  routingNumber: null,
  notes: '',
  trustLevel: TRUST_LEVEL.Untrusted,
};

const addressValidationSchema = object().shape({
  street: conditionalField(
    string().required(() => i18next.t('formik-error.required')),
    '$labelStatus'
  ),
  houseNumber: conditionalField(
    string().required(() => i18next.t('formik-error.required')),
    '$labelStatus'
  ),
  zipCode: conditionalField(
    string().required(() => i18next.t('formik-error.required')),
    '$labelStatus'
  ),
  city: conditionalField(
    string().required(() => i18next.t('formik-error.required')),
    '$labelStatus'
  ),
  countryAlfa2: conditionalField(
    string().required(() => i18next.t('formik-error.required')),
    '$labelStatus'
  ),
  flatNumber: string().nullable(),
});

const taxOfficeValidationSchema = addressValidationSchema.shape({
  name: conditionalField(
    string().required(() => i18next.t('formik-error.required')),
    '$labelStatus'
  ),
});

const accountingTypeValidationSchema = (
  condition: (type: CUSTOMER_TYPE) => boolean
) =>
  string().when('customerType', {
    is: condition,
    then: schema => schema.required(() => i18next.t('formik-error.required')),
    otherwise: schema => schema.nullable(),
  });

const physicalPersonMatureValidationSchema = string().when('customerType', {
  is: (type: CUSTOMER_TYPE) => type === CUSTOMER_TYPE.Physical,
  then: schema =>
    schema.when('dateOfBirth', {
      is: (date: string) => isUnder18(date),
      then: schema => schema.required(() => i18next.t('formik-error.required')),
      otherwise: schema => schema.nullable(),
    }),
  otherwise: schema => schema.nullable(),
});

const labelValidationSchema = object().shape({
  labelStatus: requiredEnumSelect(LABEL_STATUS),
  customerType: requiredEnumSelect(CUSTOMER_TYPE),
  labelName: string().required(() => i18next.t('formik-error.required')),
  email: conditionalField(
    string()
      .email(() => i18next.t('formik-error.invalid-email'))
      .required(() => i18next.t('formik-error.required')),
    'labelStatus',
    [-1, 1],
    string()
      .email(() => i18next.t('formik-error.invalid-email'))
      .optional()
      .nullable()
  ),

  phoneNumberPrefix: string()
    .nullable()
    .test(
      'prefix-required-if-number',
      i18next.t('formik-error.required'),
      function (value) {
        const { phoneNumber } = this.parent;
        const hasPhoneNumber =
          typeof phoneNumber === 'string' && phoneNumber.trim() !== '';
        const hasPrefix = typeof value === 'string' && value.trim() !== '';

        // If phoneNumber is filled, phoneNumberPrefix must also be filled
        if (hasPhoneNumber && !hasPrefix) {
          return this.createError({
            path: this.path,
            message: i18next.t('formik-error.required'),
          });
        }

        return true;
      }
    ),

  phoneNumber: string()
    .nullable()
    .test(
      'number-required-if-prefix',
      i18next.t('formik-error.required'),
      function (value) {
        const { phoneNumberPrefix } = this.parent;
        const hasPrefix =
          typeof phoneNumberPrefix === 'string' &&
          phoneNumberPrefix.trim() !== '';
        const hasPhoneNumber = typeof value === 'string' && value.trim() !== '';

        if (hasPrefix && !hasPhoneNumber) {
          return this.createError({
            path: this.path,
            message: i18next.t('formik-error.required'),
          });
        }

        return true;
      }
    ),

  firstName: conditionalField(
    string().required(() => i18next.t('formik-error.required')),
    'labelStatus'
  ),
  lastName: conditionalField(
    string().required(() => i18next.t('formik-error.required')),
    'labelStatus'
  ),
  dateOfBirth: conditionalField(
    string().required(() => i18next.t('formik-error.required')),
    'labelStatus'
  ),
  address: addressValidationSchema,
  taxOffice: taxOfficeValidationSchema,
  bankAccountNumber: conditionalField(
    string().required(() => i18next.t('formik-error.required')),
    'labelStatus'
  ),
  bankName: conditionalField(
    string().required(() => i18next.t('formik-error.required')),
    'labelStatus'
  ),
  personalIdCountryAlfa2: conditionalField(
    accountingTypeValidationSchema(value => value === CUSTOMER_TYPE.Physical),
    'labelStatus'
  ),
  personalId: conditionalField(
    accountingTypeValidationSchema(value => value === CUSTOMER_TYPE.Physical),
    'labelStatus'
  ),
  companyId: conditionalField(
    accountingTypeValidationSchema(value => value === CUSTOMER_TYPE.Company),
    'labelStatus'
  ),
  companyCountryAlfa2: conditionalField(
    accountingTypeValidationSchema(value => value === CUSTOMER_TYPE.Company),
    'labelStatus'
  ),
  settlementMethod: conditionalField(
    requiredEnumSelect(SETTLEMENT_METHOD),
    'labelStatus'
  ),
  legalSupervisorFirstName: conditionalField(
    physicalPersonMatureValidationSchema,
    'labelStatus'
  ),
  legalSupervisorLastName: conditionalField(
    physicalPersonMatureValidationSchema,
    'labelStatus'
  ),
  legalSupervisorCountryAlfa2: conditionalField(
    physicalPersonMatureValidationSchema,
    'labelStatus'
  ),
  legalSupervisorPersonalId: conditionalField(
    physicalPersonMatureValidationSchema,
    'labelStatus'
  ),
  swift: conditionalField(
    string().required(() => i18next.t('formik-error.required')),
    'labelStatus'
  ),
  notes: string().nullable(),
  trustLevel: requiredEnumSelect(TRUST_LEVEL),
});

export interface ILabelFormProps
  extends FormikConfig<ILabelForm>,
    IFormProps,
    II18nextPrefix {}

const LabelForm = ({
  i18nextPrefix,
  topbarContent,
  tittle,
  disableTabs,
  backPath,
  customBackItem,
  initialValues = labelInitialValues,
  status,
  ...props
}: ILabelFormProps) => {
  const MAX_SETTLEMENT_DEDUCTION_LABEL_LENGTH = 52;
  const { t } = useTranslation();
  const licenceConfiguration = useGetLicenceConfiguration();
  const applicationSettings = getApplicationSettings();
  const isSettlementDeductionEnabled =
    applicationSettings?.enableSettlementDeduction === true;
  const settlementDeductionLabelFromSettings =
    applicationSettings?.settlementDeductionName;
  const settlementDeductionLabel =
    settlementDeductionLabelFromSettings &&
    settlementDeductionLabelFromSettings.length >
      MAX_SETTLEMENT_DEDUCTION_LABEL_LENGTH
      ? `${settlementDeductionLabelFromSettings.slice(0, MAX_SETTLEMENT_DEDUCTION_LABEL_LENGTH)}...`
      : settlementDeductionLabelFromSettings ||
        t('input.label.settlementDeductionPercentage');
  const settlementDeductionPlaceholder = `(${
    applicationSettings?.settlementDeductionPercentage ?? 0
  }%)`;
  const initialValuesForFormik =
    status?.displayMode &&
    initialValues.settlementDeductionPercentage === null &&
    applicationSettings?.settlementDeductionPercentage !== null &&
    applicationSettings?.settlementDeductionPercentage !== undefined
      ? {
          ...initialValues,
          settlementDeductionPercentage:
            applicationSettings.settlementDeductionPercentage,
        }
      : initialValues;
  const { getSelectOptions, getStatusOptions } = useEnumToSelectOptions();
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const isPhysicalPersonUnmature = (
    values: Pick<ILabelForm, 'customerType' | 'dateOfBirth'>
  ) =>
    !!(
      values.customerType === CUSTOMER_TYPE.Physical &&
      values.dateOfBirth &&
      isUnder18(values.dateOfBirth)
    );

  return (
    <Formik
      {...props}
      initialValues={initialValuesForFormik}
      validationSchema={labelValidationSchema}
      initialStatus={status}
      validateOnChange={true}
      validateOnBlur={true}
      enableReinitialize={true}>
      {({
        values,
        setFieldValue,
        status,
        errors,
      }: IFormikProps<ILabelForm>) => (
        <Form className='w-full h-full'>
          <TopbarTabs>
            <TopBar
              title={status?.displayMode ? tittle : undefined}
              customBackItem={customBackItem}
              path={backPath}
              topbarContent={
                <div className=' w-full flex justify-end'>
                  {status?.displayMode ? null : (
                    <Input.ComplexWrapper sizes={[70, 30]} className='w-96'>
                      <FormInput.Text
                        name='labelName'
                        label={t('input.label.name')}
                        autoFocus
                      />
                    </Input.ComplexWrapper>
                  )}
                  <div className='flex items-center gap-2 w-full justify-end'>
                    {status?.displayMode ? (
                      <LabeledLabelStatus status={initialValues.labelStatus} />
                    ) : (
                      <div className='w-48'>
                        <FormInput.Select
                          name='labelStatus'
                          label={t('input.label.status')}
                          options={getStatusOptions(
                            LABEL_STATUS,
                            initialValues.labelStatus,
                            'enum.LABEL_STATUS'
                          )}
                        />
                      </div>
                    )}
                    <div>{topbarContent}</div>
                  </div>
                </div>
              }>
              <InfoBox loading={status?.isLoading}>
                <InfoBox.Items columns={3}>
                  <FormInput.Select
                    label={t('input.label.customerType')}
                    name='customerType'
                    onChange={option => {
                      setFieldValue('customerType', option.value);
                      if (
                        option.value === CUSTOMER_TYPE.Company &&
                        getLastPathname(pathname) ===
                          LabelTabs[LabelTabs.supervisor]
                      ) {
                        navigate(LabelTabs[LabelTabs.finances]);
                      }
                    }}
                    isDisabled={
                      initialValues.labelStatus !== LABEL_STATUS.Draft &&
                      initialValues.customerType === CUSTOMER_TYPE.Company
                    }
                    options={getSelectOptions(CUSTOMER_TYPE)}
                  />

                  <FormInput.Text
                    name='firstName'
                    label={t('input.label.firstName')}
                  />
                  <FormInput.Text
                    name='lastName'
                    label={t('input.label.lastName')}
                  />
                  <FormInput.Text name='email' label={t('input.label.email')} />
                  <Input.ComplexWrapper sizes={[40, 60]}>
                    <FormInput.Select
                      name='phoneNumberPrefix'
                      label={t('input.label.phoneNumberCodeShort')}
                      placeholder=''
                      isCreatable
                      options={getCountriesPhoneNumberPrefixOptionValues()}
                    />
                    <FormInput.Text
                      name='phoneNumber'
                      label={t('input.label.phoneNumber')}
                    />
                  </Input.ComplexWrapper>
                  {values.customerType === CUSTOMER_TYPE.Physical && (
                    <FormInput.Text
                      max={new Date().toISOString().split('T')[0]}
                      min={'1900-01-01'}
                      onChange={e => {
                        const targetValue = e.target.value;
                        setFieldValue('dateOfBirth', targetValue);
                        if (
                          !isUnder18(targetValue) &&
                          getLastPathname(pathname) ===
                            LabelTabs[LabelTabs.supervisor]
                        ) {
                          navigate(LabelTabs[LabelTabs.finances]);
                        }
                      }}
                      label={t('input.label.dateOfBirth')}
                      type='date'
                      name='dateOfBirth'
                    />
                  )}
                  <FormInput.Select
                    name='settlementMethod'
                    label={t('input.label.settlement-method')}
                    options={getSelectOptions(
                      SETTLEMENT_METHOD,
                      'enum.SETTLEMENT_METHOD'
                    )}
                  />
                  {isSettlementDeductionEnabled && (
                    <FormInput.Number
                      name='settlementDeductionPercentage'
                      label={settlementDeductionLabel}
                      placeholder={settlementDeductionPlaceholder}
                      suffix=' %'
                    />
                  )}
                  <FormInput.Select
                    name='notifyOnDocumentGeneration'
                    label={t('input.label.notify-on-document-generation')}
                    options={[
                      { label: t('input.option.yes'), value: true },
                      { label: t('input.option.no'), value: false },
                    ]}
                  />
                  {licenceConfiguration.data?.clientModuleAccess && (
                    <FormInput.Select
                      name='trustLevel'
                      label={t('input.label.trust-level')}
                      options={getSelectOptions(TRUST_LEVEL, 'enum.TRUST_LEVEL')}
                    />
                  )}
                </InfoBox.Items>
              </InfoBox>
              <TabsNavigation
                tabs={[
                  {
                    value: LabelTabs.finances,
                    name: 'finances',
                    error: getTabError([
                      errors.bankAccountNumber,
                      errors.bankName,
                      errors.personalIdCountryAlfa2,
                      errors.personalId,
                      errors.companyCountryAlfa2,
                      errors.companyId,
                      errors.address?.city,
                      errors.address?.countryAlfa2,
                      errors.address?.houseNumber,
                      errors.address?.zipCode,
                      errors.address?.street,
                      errors.address?.flatNumber,
                      errors.taxOffice?.city,
                      errors.taxOffice?.countryAlfa2,
                      errors.taxOffice?.houseNumber,
                      errors.taxOffice?.zipCode,
                      errors.taxOffice?.street,
                      errors.taxOffice?.flatNumber,
                    ]),
                  },
                  {
                    value: LabelTabs.remarks,
                    name: 'remarks',
                    error: errors.notes,
                  },
                  ...(isPhysicalPersonUnmature(values)
                    ? [
                        {
                          value: LabelTabs.supervisor,
                          name: 'supervisor',
                          error: getTabError([
                            errors.legalSupervisorFirstName,
                            errors.legalSupervisorCountryAlfa2,
                            errors.legalSupervisorLastName,
                            errors.legalSupervisorPersonalId,
                          ]),
                        },
                      ]
                    : []),
                  {
                    value: LabelTabs.residenceCertificates,
                    name: 'certificates',
                    warning:
                      values.residenceCertificateExpirationStatus?.id ===
                      RESIDENCE_CERTIFICATE_EXPIRATION_STATUS.ExpiringSoon
                        ? t('input.label.certificate-expiring-soon')
                        : undefined,
                    error:
                      values.residenceCertificateExpirationStatus?.id ===
                      RESIDENCE_CERTIFICATE_EXPIRATION_STATUS.Expired
                        ? t('input.label.certificate-expired')
                        : undefined,
                  },
                  {
                    value: LabelTabs.contracts,
                    name: 'contract',
                    permissionName: 'ContractPermissionsLIST**',
                  },
                  ...(licenceConfiguration.data?.clientModuleAccess === true
                    ? [
                        {
                          value: LabelTabs.defaultContracts,
                          name: 'default-contracts',
                          permissionName: 'ContractPermissionsLIST**',
                        },
                      ]
                    : []),
                  {
                    value: LabelTabs.advance,
                    name: 'advance',
                    permissionName: 'AdvancePermissionsLIST**',
                  },
                  {
                    value: LabelTabs.balance,
                    name: 'balance',
                    permissionName: 'SettlementCostPermissionsLIST**',
                  },
                  {
                    value: LabelTabs.reports,
                    name: 'reports',
                    permissionName: 'LabelReportOrderPermissionsLIST**',
                  },
                  {
                    value: LabelTabs.settlements,
                    name: 'settlements',
                    permissionNames: [
                      'SettlementPermissionsLIST**',
                      'SettlementOrderPermissionsLIST**',
                    ],
                  },
                  ...(licenceConfiguration.data?.clientModuleAccess === true
                    ? [
                        {
                          value: LabelTabs.threads,
                          name: 'threads',
                        },
                      ]
                    : []),
                  {
                    value: LabelTabs.statistics,
                    name: 'statistics',
                    permissionName: 'StatisticsPermissionsLIST**',
                  },
                ]}
                disabled={disableTabs}
                i18nextPrefix={i18nextPrefix}
              />
            </TopBar>
          </TopbarTabs>
        </Form>
      )}
    </Formik>
  );
};

LabelForm.getRouterTabs = (disabledChildren?: string[]) =>
  [
    { path: 'finances', element: <LabelFinances /> },
    { path: 'remarks', element: <LabelRemarks /> },
    { path: 'supervisor', element: <LabelSupervisor /> },
    { path: 'certificates', element: <LabelResidenceCertificate /> },
    { path: 'contract', element: <LabelContracts /> },
    { path: 'default-contracts', element: <LabelDefaultContracts /> },
    { path: 'balance', element: <LabelBalance /> },
    { path: 'balance/history', element: <LabelBalanceHistory /> },
    { path: 'advance', element: <LabelAdvance /> },
    { path: 'advance/history', element: <LabelAdvanceHistory /> },
    { path: 'settlements', element: <LabelSettlements /> },
    { path: 'reports', element: <LabelReports /> },
    { path: 'reports/history', element: <LabelReportsHistory /> },
    { path: 'threads', element: <LabelThreads /> },
    { path: 'statistics', element: <LabelStatistics /> },
  ].filter(children => !disabledChildren?.includes(children.path));

export default LabelForm;
